import type { IEditorVariable } from "@bbj-types/utils";
import type { Editor as TinyMCEEditor } from "@tinymce/tinymce-react";

import {
  createFieldSpan,
  createListEndSpan,
  createListStartSpan,
  createSimpleVariableSpan,
  getListEndLabel,
} from "./variableTransformation";

const TEXT_NODE = 3;
const SIMPLE_VARIABLE_PATTERN = /(\[?)\[([^[\]]+)\](\]?)$/;

type TinyMCEEditorInstance = Exclude<TinyMCEEditor["editor"], undefined>;

const getCollapsedTextContext = (range: Range): { node: Text; offset: number } | null => {
  if (!range.collapsed) {
    return null;
  }

  if (range.startContainer.nodeType === TEXT_NODE) {
    return {
      node: range.startContainer as Text,
      offset: range.startOffset,
    };
  }

  const previousSiblingIndex = range.startOffset - 1;
  if (previousSiblingIndex >= 0) {
    const previousSibling = range.startContainer.childNodes[previousSiblingIndex];
    if (previousSibling?.nodeType === TEXT_NODE) {
      const previousText = previousSibling as Text;
      return {
        node: previousText,
        offset: previousText.data.length,
      };
    }
  }

  const nextSibling = range.startContainer.childNodes[range.startOffset];
  if (nextSibling?.nodeType === TEXT_NODE) {
    return {
      node: nextSibling as Text,
      offset: 0,
    };
  }

  return null;
};

const resolveSpanForLabel = (label: string, variableList: IEditorVariable[], isRaw: boolean): string | null => {
  const simpleVariable = variableList.find((candidate) => !candidate.variables && candidate.label === label);
  if (simpleVariable) {
    return createSimpleVariableSpan(simpleVariable.label, simpleVariable.value, isRaw);
  }

  for (const variable of variableList) {
    if (!variable.variables) {
      continue;
    }

    if (!isRaw && variable.label === label) {
      return createListStartSpan(variable.value, variable.label);
    }

    const endLabel = getListEndLabel(variable.label);
    if (!isRaw && endLabel === label) {
      return createListEndSpan(variable.value, variable.label);
    }

    const field = variable.variables.find((fieldVariable) => fieldVariable.label === label);
    if (field) {
      return createFieldSpan(field.value, field.label, isRaw);
    }
  }

  return null;
};

export const convertManualVariableAtCaret = (
  editor: TinyMCEEditorInstance,
  variableList?: IEditorVariable[]
): boolean => {
  if (!variableList?.length) {
    return false;
  }

  const selection = editor.selection;
  if (!selection) {
    return false;
  }

  const range = selection.getRng();
  const context = getCollapsedTextContext(range);
  if (!context) {
    return false;
  }

  const { node, offset } = context;
  const textBeforeCaret = node.data.slice(0, offset);
  const match = textBeforeCaret.match(SIMPLE_VARIABLE_PATTERN);

  if (!match) {
    return false;
  }

  const [, openingBracket, label, closingBracket] = match;
  if (Boolean(openingBracket) !== Boolean(closingBracket)) {
    return false;
  }

  const spanHtml = resolveSpanForLabel(label, variableList, Boolean(openingBracket));
  if (!spanHtml) {
    return false;
  }

  const doc = editor.getDoc();
  if (!doc) {
    return false;
  }

  const matchStartIndex = textBeforeCaret.length - match[0].length;
  const replaceRange = doc.createRange();
  replaceRange.setStart(node, matchStartIndex);
  replaceRange.setEnd(node, offset);

  const wrapper = doc.createElement("span");
  wrapper.innerHTML = spanHtml;
  const spanElement = wrapper.firstElementChild as HTMLElement | null;

  if (!spanElement) {
    return false;
  }

  editor.undoManager.transact(() => {
    replaceRange.deleteContents();
    replaceRange.insertNode(spanElement);
    selection.select(spanElement);
    selection.collapse(false);
    editor.nodeChanged();
  });

  return true;
};

const isBracketKey = (event: KeyboardEvent): boolean => {
  if (event.key === "]" || event.code === "BracketRight") {
    return true;
  }

  if (event.key === ")" && (event.getModifierState?.("AltGraph") || event.getModifierState?.("Control"))) {
    return true;
  }

  return false;
};

interface IRegisterManualHandlersOptions {
  editor: TinyMCEEditorInstance;
  getVariableList: () => IEditorVariable[] | undefined;
  syncEditorState: (html: string, editor: TinyMCEEditorInstance) => void;
  onManualConversionStart: () => void;
}

export const registerManualVariableHandlers = ({
  editor,
  getVariableList,
  syncEditorState,
  onManualConversionStart,
}: IRegisterManualHandlersOptions): (() => void) => {
  let pendingManualVariable = false;

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      return;
    }

    if (isBracketKey(event)) {
      pendingManualVariable = true;
      onManualConversionStart();
    }
  };

  const handleBracketInput = () => {
    if (!pendingManualVariable) {
      return;
    }

    setTimeout(() => {
      const converted = convertManualVariableAtCaret(editor, getVariableList());
      pendingManualVariable = false;

      if (!converted) {
        syncEditorState(editor.getContent({ format: "html" }), editor);
      }
    }, 0);
  };

  editor.on("keydown", handleKeydown);
  editor.on("input", handleBracketInput);

  return () => {
    editor.off("keydown", handleKeydown);
    editor.off("input", handleBracketInput);
  };
};
